import React from "react";
import cn from "./helpers/cn";

export type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl";

// MUI v4 default theme breakpoints (não os do Tailwind!).
const breakpointValues: Record<Breakpoint, number> = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

// Próximo breakpoint depois deste (usado para calcular ranges em `Down`/`only`).
const nextBreakpointValue: Record<Breakpoint, number> = {
  xs: 600,
  sm: 960,
  md: 1280,
  lg: 1920,
  xl: Infinity,
};

interface HiddenProps {
  children?: React.ReactNode;
  /** "css" usa media queries do Tailwind (default). "js" remove o nó via matchMedia. */
  implementation?: "css" | "js";

  /** Esconde em telas <  breakpoint (xsDown = xs only; smDown = xs+sm; etc.). */
  xsDown?: boolean;
  smDown?: boolean;
  mdDown?: boolean;
  lgDown?: boolean;
  xlDown?: boolean;

  /** Esconde em telas >= breakpoint. xsUp = sempre escondido. */
  xsUp?: boolean;
  smUp?: boolean;
  mdUp?: boolean;
  lgUp?: boolean;
  xlUp?: boolean;

  /** Esconde só num breakpoint específico (ou em vários, via array). */
  only?: Breakpoint | Breakpoint[];

  /** Largura inicial usada no SSR antes de o matchMedia rodar (modo "js"). */
  initialWidth?: Breakpoint;
}

const ORDER: Breakpoint[] = ["xs", "sm", "md", "lg", "xl"];

// Resolve as classes Tailwind que escondem o conteúdo nas faixas certas.
// Usa media queries arbitrárias com os valores do MUI v4.
function buildClassNames(props: HiddenProps): string {
  const classes: string[] = [];

  // *Down — esconde de 0 até o próximo breakpoint (não inclusive).
  for (const bp of ORDER) {
    const downKey = `${bp}Down` as keyof HiddenProps;

    if (!props[downKey]) continue;
    const upperBound = nextBreakpointValue[bp]; // limite superior (exclusivo)

    if (upperBound === Infinity) {
      // xlDown -> sempre escondido
      classes.push("hidden");
    } else {
      // Esconde abaixo de `upperBound`: usa `max-[Npx]:hidden`.
      classes.push(`max-[${upperBound - 0.05}px]:hidden`);
    }
  }

  // *Up — esconde a partir do breakpoint.
  for (const bp of ORDER) {
    const upKey = `${bp}Up` as keyof HiddenProps;

    if (!props[upKey]) continue;
    const lowerBound = breakpointValues[bp];

    if (lowerBound === 0) {
      // xsUp -> sempre escondido
      classes.push("hidden");
    } else {
      classes.push(`min-[${lowerBound}px]:hidden`);
    }
  }

  // only — esconde no intervalo [breakpoint, próximo).
  const onlyList = props.only
    ? Array.isArray(props.only) ? props.only : [props.only]
    : [];

  for (const bp of onlyList) {
    const lower = breakpointValues[bp];
    const upper = nextBreakpointValue[bp];

    if (lower === 0 && upper === Infinity) {
      classes.push("hidden");
    } else if (lower === 0) {
      classes.push(`max-[${upper - 0.05}px]:hidden`);
    } else if (upper === Infinity) {
      classes.push(`min-[${lower}px]:hidden`);
    } else {
      // Intervalo: precisamos esconder >= lower E < upper. Tailwind não tem
      // sintaxe combinada em uma única classe — usa duas com a media-query
      // composta entre colchetes.
      classes.push(`[@media(min-width:${lower}px)_and_(max-width:${upper - 0.05}px)]:hidden`);
    }
  }

  return classes.join(" ");
}

// Resolve em JS se o conteúdo deve estar escondido AGORA (modo "js").
function shouldHideJs(props: HiddenProps, currentWidth: number): boolean {
  for (const bp of ORDER) {
    if (props[`${bp}Down` as keyof HiddenProps] && currentWidth < nextBreakpointValue[bp]) return true;
    if (props[`${bp}Up` as keyof HiddenProps] && currentWidth >= breakpointValues[bp]) return true;
  }

  const onlyList = props.only ? (Array.isArray(props.only) ? props.only : [props.only]) : [];

  for (const bp of onlyList) {
    if (currentWidth >= breakpointValues[bp] && currentWidth < nextBreakpointValue[bp]) return true;
  }

  return false;
}

const Hidden: React.FC<HiddenProps> = (props) => {
  const { implementation = "css", initialWidth = "lg", children } = props;

  // Modo CSS: envolve filhos num span (display:contents) com as classes hidden.
  // contents preserva o layout original dos filhos.
  if (implementation === "css") {
    const classes = buildClassNames(props);

    if (!classes) return <>{children}</>;

    return <span className={cn("contents", classes)}>{children}</span>;
  }

  // Modo JS: usa matchMedia para decidir se renderiza.
  return <HiddenJs {...props} initialWidth={initialWidth}>{children}</HiddenJs>;
};

const HiddenJs: React.FC<HiddenProps & { initialWidth: Breakpoint }> = (props) => {
  const { initialWidth, children } = props;
  const [width, setWidth] = React.useState<number>(() => {
    if (typeof window === "undefined") return breakpointValues[initialWidth];

    return window.innerWidth;
  });

  React.useEffect(() => {
    const handler = () => setWidth(window.innerWidth);

    window.addEventListener("resize", handler);

    return () => window.removeEventListener("resize", handler);
  }, []);

  if (shouldHideJs(props, width)) return null;

  return <>{children}</>;
};

export default Hidden;
